import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  ShoppingBag,
  UtensilsCrossed,
  ChefHat,
  BarChart3,
  Users,
  UserCog,
  Settings,
  LogOut,
  Mail,
  Zap,
  QrCode,
  Plug,
  FileText,
  Receipt,
  Smartphone,
  CalendarDays,
  BookOpen,
  RefreshCw,
  Megaphone,
  Clock,
  Palette,
  Crown,
  Gift,
  Ticket,
  Wallet,
  Send,
  Award,
  ClipboardList,
  Timer,
  Eye,
  Share2,
  Monitor,
  Image,
  ListVideo,
  CalendarClock,
  LayoutGrid,
  AlertTriangle,
  FolderOpen,
  Activity,
  Brain,
  DollarSign,
  TrendingUp,
  Wand2,
  Database,
  Leaf,
  UserCheck,
  CalendarOff,
  Briefcase,
  CalendarCheck,
  Box,
  Wrench,
  Package,
  FileUp,
  Truck,
  PartyPopper,
  MessageSquare,
  Gavel,
  Shield,
  MapPin,
  ShieldCheck,
  Navigation,
  WifiOff,
  FolderKanban,
  ListTodo,
  FileSignature,
  ExternalLink,
  Tv,
  ShoppingCart,
  ChevronRight,
  ChevronUp,
  Globe,
  Rocket,
  Tag,
  User,
  PanelLeft,
  PanelLeftClose,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import appLogo from "@/assets/logo-icon-white.png";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/rc/hooks/profile/use-profile";
import { useLogout } from "@/hooks/use-logout";
import { useRestaurantStore } from "@/stores/restaurantStore";
import { isPathAllowedForRole } from "@/lib/auth/routePermissions";
import { isSuperAdmin } from "@/lib/auth/roles";
import { getDisplayInitial } from "@/utils/displayInitial";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RestaurantSwitcher } from "@/components/auth/RestaurantSwitcher";
import {
  SidebarNavGroup,
  type SidebarNavMenuGroup,
} from "@/components/admin/SidebarNavGroup";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const menuGroups: SidebarNavMenuGroup[] = [
  {
    labelKey: "sidebar.overview",
    icon: LayoutDashboard,
    items: [
      {
        titleKey: "sidebar.dashboard",
        url: "/admin",
        icon: LayoutDashboard,
      },
    ],
  },
  {
    labelKey: "sidebar.sales",
    icon: ShoppingBag,
    items: [
      { titleKey: "sidebar.orders", url: "/admin/orders", icon: ShoppingBag },
      {
        titleKey: "sidebar.orderTypes",
        url: "/admin/order-types",
        icon: ShoppingBag,
      },
      {
        titleKey: "sidebar.deliveryTracking",
        url: "/admin/delivery-tracking",
        icon: Navigation,
      },
      {
        titleKey: "sidebar.happyHour",
        url: "/admin/happy-hour",
        icon: Clock,
      },
    ],
  },
  {
    labelKey: "sidebar.menuAndKitchen",
    icon: UtensilsCrossed,
    items: [
      { titleKey: "sidebar.menu", url: "/admin/menu", icon: UtensilsCrossed },
      {
        titleKey: "sidebar.articleImport",
        url: "/admin/article-import",
        icon: FileUp,
      },
      {
        titleKey: "sidebar.inventory",
        url: "/admin/inventory",
        icon: Package,
      },
      {
        titleKey: "sidebar.timestampMenu",
        url: "/admin/timestamp-menu",
        icon: Clock,
      },
      {
        titleKey: "sidebar.menuStyle",
        url: "/admin/menu-style",
        icon: Palette,
      },
      {
        titleKey: "sidebar.lunchSchedule",
        url: "/admin/lunch-schedule",
        icon: CalendarDays,
      },
      {
        titleKey: "sidebar.menuDesigner",
        url: "/admin/menu-designer",
        icon: FileText,
      },
      {
        titleKey: "sidebar.kitchenDisplay",
        url: "/admin/kitchen",
        icon: ChefHat,
      },
    ],
  },
  {
    labelKey: "sidebar.bookingsAndTables",
    icon: BookOpen,
    items: [
      {
        titleKey: "sidebar.overviewShort",
        url: "/admin/bookings-overview",
        icon: Eye,
      },
      {
        titleKey: "sidebar.bookings",
        url: "/admin/reservations",
        icon: BookOpen,
      },
      {
        titleKey: "sidebar.bookingSettings",
        url: "/admin/reservation-settings",
        icon: CalendarDays,
      },
      { titleKey: "sidebar.tableMap", url: "/admin/tables", icon: QrCode },
    ],
  },
  {
    labelKey: "sidebar.ai",
    icon: Brain,
    items: [
      {
        titleKey: "sidebar.aiDashboard",
        url: "/admin/ai/dashboard",
        icon: Brain,
      },
      {
        titleKey: "sidebar.priceIntelligence",
        url: "/admin/ai/price-intelligence",
        icon: DollarSign,
      },
      {
        titleKey: "sidebar.trendAnalysis",
        url: "/admin/ai/trends",
        icon: TrendingUp,
      },
      {
        titleKey: "sidebar.menuOptimizer",
        url: "/admin/ai/menu-optimizer",
        icon: ChefHat,
      },
      {
        titleKey: "sidebar.recipeGenerator",
        url: "/admin/ai/recipes",
        icon: Wand2,
      },
      { titleKey: "sidebar.wasteAnalysis", url: "/admin/ai/waste", icon: Leaf },
      {
        titleKey: "sidebar.staffOptimizer",
        url: "/admin/ai/staff-optimizer",
        icon: Users,
      },
      {
        titleKey: "sidebar.customerInsights",
        url: "/admin/ai/customer-insights",
        icon: UserCheck,
      },
      {
        titleKey: "sidebar.analysisHistory",
        url: "/admin/ai/history",
        icon: Database,
      },
    ],
  },
  {
    labelKey: "sidebar.financeAndReports",
    icon: BarChart3,
    items: [
      {
        titleKey: "sidebar.analytics",
        url: "/admin/analytics",
        icon: BarChart3,
      },
      { titleKey: "sidebar.reports", url: "/admin/reports", icon: FileText },
      { titleKey: "sidebar.invoices", url: "/admin/invoices", icon: Receipt },
    ],
  },
  {
    labelKey: "sidebar.customersAndMarketing",
    icon: Users,
    items: [
      { titleKey: "sidebar.customers", url: "/admin/customers", icon: Users },
      {
        titleKey: "sidebar.members",
        url: "/admin/loyalty/members",
        icon: Crown,
      },
      {
        titleKey: "sidebar.programSettings",
        url: "/admin/loyalty/settings",
        icon: Settings,
      },
      {
        titleKey: "sidebar.programPush",
        url: "/admin/loyalty/push",
        icon: Send,
      },
      { titleKey: "sidebar.topUp", url: "/admin/loyalty/topup", icon: Wallet },
      {
        titleKey: "sidebar.vouchers",
        url: "/admin/loyalty/vouchers",
        icon: Ticket,
      },
      {
        titleKey: "sidebar.rewards",
        url: "/admin/loyalty/rewards",
        icon: Gift,
      },
      {
        titleKey: "sidebar.redemptions",
        url: "/admin/loyalty/redemptions",
        icon: Award,
      },
      {
        titleKey: "sidebar.emailCampaigns",
        url: "/admin/email-campaigns",
        icon: Mail,
      },
      {
        titleKey: "sidebar.socialMedia",
        url: "/admin/social-media",
        icon: Share2,
      },
      {
        titleKey: "sidebar.automations",
        url: "/admin/notification-templates",
        icon: Zap,
      },
      {
        titleKey: "sidebar.marketing",
        url: "/admin/marketing",
        icon: Megaphone,
      },
    ],
  },
  {
    labelKey: "sidebar.pos",
    icon: Smartphone,
    items: [
      {
        titleKey: "sidebar.terminal",
        url: "/admin/terminal",
        icon: Smartphone,
      },
      {
        titleKey: "sidebar.customerCartScreen",
        url: "/admin/customer-display",
        icon: Monitor,
      },
      {
        titleKey: "sidebar.expressCheckout",
        url: "/admin/express-checkout",
        icon: Zap,
      },
      {
        titleKey: "sidebar.terminalSettings",
        url: "/admin/terminal-settings",
        icon: Settings,
      },
    ],
  },
  {
    labelKey: "sidebar.staff",
    icon: UserCog,
    items: [
      { titleKey: "sidebar.staffMain", url: "/admin/staff", icon: UserCog },
      {
        titleKey: "sidebar.staffSchedule",
        url: "/admin/staff/schedule",
        icon: CalendarDays,
      },
      {
        titleKey: "sidebar.timeClock",
        url: "/admin/staff/timeclock",
        icon: Timer,
      },
      {
        titleKey: "sidebar.staffLeave",
        url: "/admin/staff/leave",
        icon: CalendarOff,
      },
      {
        titleKey: "sidebar.staffOverview",
        url: "/admin/staff/overview",
        icon: Eye,
      },
    ],
  },
  {
    labelKey: "sidebar.servicesAndBooking",
    icon: Briefcase,
    items: [
      { titleKey: "sidebar.services", url: "/admin/services", icon: Briefcase },
      {
        titleKey: "sidebar.resources",
        url: "/admin/services/resources",
        icon: Box,
      },
      {
        titleKey: "sidebar.bookingCalendar",
        url: "/admin/services/calendar",
        icon: CalendarCheck,
      },
      {
        titleKey: "sidebar.serviceBookings",
        url: "/admin/services/bookings",
        icon: ClipboardList,
      },
      {
        titleKey: "sidebar.serviceSettings",
        url: "/admin/services/settings",
        icon: Wrench,
      },
    ],
  },
  {
    labelKey: "sidebar.digitalSignage",
    icon: Monitor,
    items: [
      {
        titleKey: "sidebar.screens",
        url: "/admin/signage/devices",
        icon: Monitor,
      },
      {
        titleKey: "sidebar.groups",
        url: "/admin/signage/groups",
        icon: FolderOpen,
      },
      {
        titleKey: "sidebar.mediaLibrary",
        url: "/admin/signage/assets",
        icon: Image,
      },
      {
        titleKey: "sidebar.playlists",
        url: "/admin/signage/playlists",
        icon: ListVideo,
      },
      {
        titleKey: "sidebar.scheduling",
        url: "/admin/signage/schedules",
        icon: CalendarClock,
      },
      {
        titleKey: "sidebar.zones",
        url: "/admin/signage/zones",
        icon: LayoutGrid,
      },
      {
        titleKey: "sidebar.alerts",
        url: "/admin/signage/alerts",
        icon: AlertTriangle,
      },
      {
        titleKey: "sidebar.analyticsShort",
        url: "/admin/signage/analytics",
        icon: Activity,
      },
    ],
  },
  {
    labelKey: "sidebar.projectsAndQuotes",
    icon: FolderKanban,
    items: [
      {
        titleKey: "sidebar.projects",
        url: "/admin/projects",
        icon: FolderKanban,
      },
      { titleKey: "sidebar.todo", url: "/admin/todo", icon: ListTodo },
      { titleKey: "sidebar.quotes", url: "/admin/quotes", icon: FileSignature },
    ],
  },
  {
    labelKey: "sidebar.onDemand",
    icon: Rocket,
    items: [
      {
        titleKey: "sidebar.onDemandDashboard",
        url: "/admin/on-demand",
        icon: LayoutDashboard,
      },
      {
        titleKey: "sidebar.onDemandCredits",
        url: "/admin/on-demand/credits",
        icon: Wallet,
      },
      {
        titleKey: "sidebar.onDemandCatalog",
        url: "/admin/on-demand/catalog",
        icon: Package,
      },
      {
        titleKey: "sidebar.onDemandNewRequest",
        url: "/admin/on-demand/new-request",
        icon: FileText,
      },
      {
        titleKey: "sidebar.onDemandRequests",
        url: "/admin/on-demand/requests",
        icon: ClipboardList,
      },
      {
        titleKey: "sidebar.onDemandProjects",
        url: "/admin/on-demand/projects",
        icon: FolderKanban,
      },
      {
        titleKey: "sidebar.onDemandDeliverables",
        url: "/admin/on-demand/deliverables",
        icon: FolderOpen,
      },
      {
        titleKey: "sidebar.onDemandCalendar",
        url: "/admin/on-demand/calendar",
        icon: CalendarClock,
      },
      {
        titleKey: "sidebar.onDemandBrands",
        url: "/admin/on-demand/brands",
        icon: Palette,
      },
      {
        titleKey: "sidebar.onDemandOps",
        url: "/admin/on-demand/ops",
        icon: Activity,
      },
      {
        titleKey: "sidebar.onDemandSettings",
        url: "/admin/on-demand/settings",
        icon: Settings,
      },
    ],
  },
  {
    labelKey: "sidebar.inbox",
    icon: Mail,
    items: [
      {
        titleKey: "sidebar.inboxMain",
        url: "/admin/inbox",
        icon: MessageSquare,
      },
      {
        titleKey: "sidebar.inboxDecisions",
        url: "/admin/inbox/decisions",
        icon: Gavel,
      },
      {
        titleKey: "sidebar.inboxStandups",
        url: "/admin/inbox/standups",
        icon: Users,
      },
      {
        titleKey: "sidebar.inboxAnalytics",
        url: "/admin/inbox/analytics",
        icon: BarChart3,
      },
    ],
  },
  {
    labelKey: "sidebar.suppliersAndEvents",
    icon: Truck,
    items: [
      { titleKey: "sidebar.suppliers", url: "/admin/suppliers", icon: Truck },
      { titleKey: "sidebar.events", url: "/admin/events", icon: PartyPopper },
    ],
  },
  {
    labelKey: "sidebar.comminovaNet",
    icon: Share2,
    items: [
      {
        titleKey: "sidebar.netOverview",
        url: "/admin/net",
        icon: LayoutDashboard,
      },
      {
        titleKey: "sidebar.netPartners",
        url: "/admin/net?tab=partners",
        icon: Users,
      },
      {
        titleKey: "sidebar.netNetwork",
        url: "/admin/net?tab=network",
        icon: Share2,
      },
      { titleKey: "sidebar.netMgm", url: "/admin/net?tab=mgm", icon: Award },
    ],
  },
];

const systemGroup: SidebarNavMenuGroup = {
  labelKey: "sidebar.system",
  icon: Settings,
  items: [
    { titleKey: "sidebar.integrations", url: "/admin/integrations", icon: Plug },
    { titleKey: "sidebar.framerSync", url: "/admin/framer-sync", icon: RefreshCw },
    { titleKey: "sidebar.roles", url: "/admin/roles", icon: ShieldCheck },
    { titleKey: "sidebar.gdpr", url: "/admin/gdpr", icon: Shield },
    { titleKey: "sidebar.locations", url: "/admin/locations", icon: MapPin },
    { titleKey: "sidebar.offline", url: "/admin/offline", icon: WifiOff },
    { titleKey: "sidebar.reviews", url: "/admin/reviews", icon: MessageSquare },
    { titleKey: "sidebar.tags", url: "/admin/tags", icon: Tag },
    { titleKey: "sidebar.settings", url: "/admin/settings", icon: Settings },
  ],
};

interface QuickLinkItem {
  titleKey: string;
  url: string;
  icon: typeof LayoutDashboard;
}

/** Public routes that accept `/:slug?` or need slug in path — see `App.tsx` SlugLayout routes. */
function buildQuickLinks(restaurantSlug: string | null): QuickLinkItem[] {
  const s = restaurantSlug?.trim() || null;
  const enc = s ? encodeURIComponent(s) : "";

  return [
    {
      titleKey: "sidebar.quickOrderPage",
      url: s ? `/order/${enc}` : "/order",
      icon: ShoppingCart,
    },
    {
      titleKey: "sidebar.quickDisplay",
      url: s ? `/display/${enc}` : "/display",
      icon: Tv,
    },
    {
      titleKey: "sidebar.quickBooking",
      url: s ? `/book/${enc}` : "/book",
      icon: CalendarDays,
    },
    {
      titleKey: "sidebar.quickServices",
      url: s ? `/services/${enc}` : "/services",
      icon: Briefcase,
    },
    {
      titleKey: "sidebar.quickSignage",
      url: "/signage/player",
      icon: Monitor,
    },
    {
      titleKey: "sidebar.quickGiftCard",
      // Route is `/gift/:slug/:code` — `demo` is a sample code for staff preview when tenant is known.
      url: s ? `/gift/${enc}/demo` : "/gift/demo",
      icon: Gift,
    },
    { titleKey: "sidebar.quickHomepage", url: "/", icon: Globe },
  ];
}

export function AppSidebar() {
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  const location = useLocation();
  const { state, isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const logout = useLogout();
  const activeRole = useRestaurantStore((s) => s.activeRole);
  const activeSlug = useRestaurantStore((s) => s.activeSlug);
  const { data: profile, isLoading: profileLoading } = useProfile();
  const superAdmin = isSuperAdmin(profile);

  // Auto-close mobile sidebar on route change
  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [location.pathname, isMobile, setOpenMobile]);

  const displayName =
    profile?.full_name?.trim() ||
    profile?.email?.trim() ||
    user?.email?.trim() ||
    "";

  const displayEmail = user?.email ?? "";
  const collapsed = state === "collapsed";
  const [systemOpen, setSystemOpen] = useState(false);
  const SystemGroupIcon = systemGroup.icon;
  const profileMenuItemClass =
    "h-8 gap-2 rounded-md px-2 py-0 text-xs font-medium text-sidebar-foreground outline-hidden transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus:bg-sidebar-accent focus:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-[highlighted]:bg-sidebar-accent data-[highlighted]:text-sidebar-accent-foreground";

  const visibleSystemItems = useMemo(() => {
    if (!activeRole && !superAdmin) return [];
    return systemGroup.items.filter((i) =>
      isPathAllowedForRole(i.url, activeRole, superAdmin)
    );
  }, [activeRole, superAdmin]);

  const visibleMenuGroups = useMemo(() => {
    if (!activeRole && !superAdmin) return [];
    return menuGroups
      .map((g) => ({
        ...g,
        items: g.items.filter((i) =>
          isPathAllowedForRole(i.url, activeRole, superAdmin)
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [activeRole, superAdmin]);

  const quickLinks = useMemo(() => buildQuickLinks(activeSlug), [activeSlug]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="border-b p-2">
        <div
          className={cn(
            "flex transition-all duration-200",
            collapsed
              ? "w-full flex-col items-center gap-2"
              : "items-center gap-2"
          )}
        >
          <div
            className={cn(
              "flex min-w-0 flex-1 items-center",
              collapsed ? "justify-center" : "gap-3"
            )}
          >
            <div
              className={cn(
                "flex shrink-0 items-center justify-center overflow-hidden bg-primary",
                collapsed ? "size-8 rounded-md" : "h-9 w-9 rounded-lg"
              )}
            >
              <img
                src={appLogo}
                alt="ComminovaOS"
                className={cn(
                  "object-contain",
                  collapsed ? "size-4" : "h-6 w-6"
                )}
              />
            </div>
            {!collapsed && (
              <div className="overflow-hidden">
                <h2 className="font-bold text-base truncate">One</h2>
                {!superAdmin && (
                  <p className="text-[10px] text-muted-foreground truncate">
                    {t("sidebar.adminPanel")}
                  </p>
                )}
                {superAdmin && (
                  <p className="mt-1 text-[10px] text-primary font-medium truncate">
                    Platform superadmin
                  </p>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/50 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          >
            {collapsed ? (
              <PanelLeft className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </button>
        </div>
        {!collapsed && (
          <RestaurantSwitcher className="mt-3 bg-white hover:bg-white" />
        )}
      </SidebarHeader>

      <SidebarContent className="gap-1 pt-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden group-data-[collapsible=icon]:overflow-y-auto">
        {visibleMenuGroups.map((group) => (
          <SidebarNavGroup
            key={group.labelKey}
            group={group}
            collapsed={collapsed}
          />
        ))}

        {/* Quick Links to public pages */}
        {collapsed ? (
          <SidebarGroup className="py-0.5">
            <SidebarGroupContent>
              <SidebarMenu className="items-center">
                <SidebarMenuItem>
                  <Popover open={quickLinksOpen} onOpenChange={setQuickLinksOpen}>
                    <PopoverTrigger asChild>
                      <SidebarMenuButton tooltip={t("sidebar.quickLinks")} className="justify-center">
                        <ExternalLink className="w-4 h-4 shrink-0" />
                      </SidebarMenuButton>
                    </PopoverTrigger>
                    <PopoverContent side="right" align="start" className="w-48 p-1.5 ml-1" onCloseAutoFocus={(e) => e.preventDefault()}>
                      <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("sidebar.quickLinks")}
                      </p>
                      <div className="mt-1 space-y-0.5">
                        {quickLinks.map((item) => {
                          const Icon = item.icon;
                          return (
                            <a
                              key={item.titleKey}
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setQuickLinksOpen(false)}
                              className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                            >
                              <Icon className="w-4 h-4 shrink-0" />
                              <span className="flex-1">{t(item.titleKey)}</span>
                              <ExternalLink className="w-3 h-3 shrink-0 opacity-40" />
                            </a>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <Collapsible className="group/collapsible">
            <SidebarGroup className="py-0.5">
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger className="flex h-8 w-full items-center gap-2 cursor-pointer rounded-md px-2 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                  <ExternalLink className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left">
                    {t("sidebar.quickLinks")}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent className="ml-3 border-l border-sidebar-border pl-2">
                  <SidebarMenu
                    className={collapsed ? "items-center" : undefined}
                  >
                    {quickLinks.map((item) => {
                      const Icon = item.icon;
                      return (
                        <SidebarMenuItem key={item.titleKey}>
                          <SidebarMenuButton
                            asChild
                            tooltip={t(item.titleKey)}
                            className="text-xs w-[90%] mx-auto"
                          >
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2"
                            >
                              <Icon className="w-4 h-4 shrink-0" />
                              <span className="flex-1">{t(item.titleKey)}</span>
                              <ExternalLink className="w-3 h-3 shrink-0 opacity-40" />
                            </a>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t px-2 py-3">
        <DropdownMenu>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "flex items-center gap-2 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-ring",
                      collapsed
                        ? "size-9 shrink-0 justify-center rounded-md border-0 bg-transparent p-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        : "w-full rounded-lg border border-sidebar-border bg-sidebar-accent/20 px-3 py-2.5 hover:bg-sidebar-accent/40"
                    )}
                  >
                    <div
                      className={cn(
                        "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary",
                        collapsed ? "size-8" : "aspect-square h-9 min-h-9 w-9 min-w-9"
                      )}
                    >
                      <span className="text-primary-foreground font-semibold text-xs">
                        {getDisplayInitial(displayName, displayEmail)}
                      </span>
                    </div>
                    {!collapsed && (
                      <>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {profileLoading ? "…" : displayName || displayEmail}
                          </p>
                          <p className="text-xs text-muted-foreground capitalize truncate">
                            {superAdmin
                              ? "platform superadmin"
                              : (activeRole ?? "—")}
                          </p>
                        </div>
                        <ChevronUp className="h-4 w-4 shrink-0 text-sidebar-foreground" />
                      </>
                    )}
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side="right">
                  {t("sidebar.myProfile")}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          <DropdownMenuContent
            side={collapsed ? "right" : "top"}
            align={collapsed ? "center" : "start"}
            className="w-56 border-sidebar-border bg-sidebar text-sidebar-foreground"
          >
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium truncate">
                {profileLoading ? "…" : displayName || displayEmail}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {displayEmail}
              </p>
            </div>
            <DropdownMenuSeparator className="bg-sidebar-border/80" />
            <DropdownMenuItem
              asChild
              variant="sidebar"
              className={cn("cursor-pointer", profileMenuItemClass)}
            >
              <Link to="/admin/profile">
                <User className="w-4 h-4 text-sidebar-foreground!" />
                {t("sidebar.myProfile")}
              </Link>
            </DropdownMenuItem>
            {visibleSystemItems.length > 0 && (
              <>
                <DropdownMenuSeparator className="bg-sidebar-border/80" />
                <Collapsible open={systemOpen} onOpenChange={setSystemOpen}>
                  <CollapsibleTrigger
                    className={cn(
                      "flex w-full items-center gap-2",
                      profileMenuItemClass
                    )}
                  >
                    <SystemGroupIcon className="w-4 h-4 shrink-0 text-sidebar-foreground!" />
                    <span className="flex-1 text-left">{t(systemGroup.labelKey)}</span>
                    <ChevronRight className={cn("w-3.5 h-3.5 shrink-0 text-sidebar-foreground! transition-transform duration-200", systemOpen && "rotate-90")} />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-4 mt-0.5 border-l border-sidebar-border pl-2">
                      {visibleSystemItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem
                            key={item.url}
                            asChild
                            variant="sidebar"
                            className={cn("cursor-pointer", profileMenuItemClass)}
                          >
                            <Link to={item.url}>
                              <Icon className="w-4 h-4 shrink-0 text-sidebar-foreground!" />
                              {t(item.titleKey)}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </>
            )}
            <DropdownMenuSeparator className="bg-sidebar-border/80" />
            <DropdownMenuItem
              onClick={handleLogout}
              variant="sidebar"
              className={cn("cursor-pointer", profileMenuItemClass)}
            >
              <LogOut className="h-4 w-4 text-sidebar-foreground!" />
              {t("sidebar.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
